<template>
  <v-dialog v-model="popupDialog" width="50%" transition="dialog-bottom-transition">
    <v-card rounded="xl">
      <v-toolbar dense="dense" text="text" color="accent white--text">
        <v-toolbar-title>Context Keys</v-toolbar-title>
        <v-spacer></v-spacer>
      </v-toolbar>

      <v-card-text class="pa-4">
        <v-autocomplete
          v-model="selected"
          :items="items"
          label="Search For A Context Key"
          clearable
          chips
          return-object
          :customFilter="customFilter"
          item-title="text"
          :item-props="true"
          variant="underlined"
          color="primary"
        >
          <template v-slot:item="{ props, item }">
            <v-list-item :value="item">
              <template v-slot:append>
                <v-list-item-action>
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        icon="mdi-content-copy"
                        @click.stop.prevent="copyContextKey(item)"
                        v-bind="props"
                        variant="text"
                      >
                      </v-btn>
                    </template>
                    <span>{{ toolTipText }}</span>
                  </v-tooltip>
                </v-list-item-action>
              </template>

              <v-list-item-title v-bind="props"
                ><v-chip>{{ item.title }}</v-chip></v-list-item-title
              >
            </v-list-item>
          </template>
        </v-autocomplete>

        <v-data-table
          v-if="isShowDataTable"
          :headers="headers"
          :items="enrichedItems"
          id="context--data-table"
          class="text-grey-darken-1 text-body-2"
          hide-default-footer
        >
          <template v-slot:item="{ item }">
            <tr>
              <td>{{ item.key }}</td>
              <td>
                <v-chip v-for="tag in item.tags" :key="tag" class="ma-1" size="small"
                  ><strong>{{ tag }}</strong></v-chip
                >
              </td>
              <td class="text-left" v-html="mdToHtml(item.keyValue)"></td>
              <td>
                <div class="d-flex">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        @click.stop.prevent="copyContextKey(item)"
                        icon="mdi-content-copy"
                        variant="text"
                      >
                      </v-btn>
                    </template>
                    <span>{{ toolTipText }}</span>
                  </v-tooltip>
                </div>
              </td>
              <td>{{ item.status }}</td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>

      <v-card-actions class="px-7 pb-7">
        <v-spacer></v-spacer>
        <cs-button
          id="selectContextKeyButton"
          label="Insert"
          @click="selectContextKey"
          primary
          large
        ></cs-button>
        <cs-button
          id="cancelAddContextKeys"
          label="Cancel"
          @click="cancelAddContextKeys"
          large
        ></cs-button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { CSBase } from '@complispace/cs-design-system';
import { mapGetters } from 'vuex';
import updateTableColumnsWidth from '@/lib/tableUtils';
import { markdownItFancyListPlugin } from 'markdown-it-fancy-lists';

const md = require('markdown-it')({
  html: true
});

md.use(markdownItFancyListPlugin);

export default {
  name: 'TipTapContextKeysDialog',
  extends: CSBase,

  props: {
    showDialog: { type: Boolean, required: true, default: false }
  },

  emits: ['closed', 'insertContextKey'],

  data() {
    return {
      headers: [
        {
          title: 'Key',
          align: 'left',
          value: 'key',
          sortable: false
        },
        {
          title: 'Tags',
          align: 'left',
          value: 'tags',
          sortable: false
        },
        {
          title: 'Value',
          align: 'left',
          value: 'html',
          sortable: false
        },
        {
          title: 'Actions',
          value: 'name',
          sortable: false
        },
        {
          title: 'Status',
          value: 'status',
          width: '96',
          sortable: false
        }
      ],
      popupDialog: false,
      selected: null
    };
  },

  computed: {
    ...mapGetters({
      contextItems: 'clientContext/items'
    }),

    isShowDataTable() {
      if (this.selected) {
        return !!this.contextItems.find((contextKey) => contextKey.id === this.selected.id);
      }

      return false;
    },

    items() {
      if (this.contextItems) {
        const mergedFieldsContextKeys = this.contextItems.filter((contextItem) => {
          const mergeField = contextItem.tags.find((tag) => tag.toLowerCase() === 'merge field');
          return mergeField;
        });

        const keyItems = mergedFieldsContextKeys.map((contextKey) => {
          return {
            id: contextKey.id,
            keySyntax: `{{ ${contextKey.key} }}`,
            text: contextKey.key,
            placeholder: contextKey.placeholder,
            keyValue: contextKey.value
          };
        });

        return keyItems;
      }

      return [];
    },

    enrichedItems() {
      const result = [];

      if (this.selected) {
        const selectedItem = this.contextItems.find(
          (contextKey) => contextKey.id === this.selected.id
        );

        if (selectedItem) {
          const status = selectedItem.value ? 'complete' : 'blank';

          result.push({
            key: selectedItem.key,
            tags: selectedItem.tags,
            keyValue: selectedItem.value,
            status,
            keySyntax: `{{ ${selectedItem.key} }}`
          });
        }
      }

      return result;
    },

    toolTipText() {
      return 'Copy Context Key Syntax';
    }
  },

  watch: {
    showDialog(value) {
      if (value) {
        this.popupDialog = true;
        this.selected = null;
      }
    },

    popupDialog(value) {
      if (!value) {
        this.$emit('closed');
      }
    }
  },

  methods: {
    customFilter(itemText, queryText, item) {
      const textIndex = itemText.toLowerCase().indexOf(queryText.toLowerCase());
      const valueIndex = item.keyValue
        ? item.keyValue.toLowerCase().indexOf(queryText.toLowerCase())
        : -1;

      return textIndex > -1 || valueIndex > -1;
    },

    mdToHtml(content) {
      if (!content) return null;

      if (content.includes('</th>') || content.includes('</td>'))
        return md.render(updateTableColumnsWidth(content));

      return md.render(content);
    },

    selectContextKey() {
      this.popupDialog = false;

      if (this.selected) {
        this.$emit('insertContextKey', this.selected.keySyntax);
      }
    },

    cancelAddContextKeys() {
      this.popupDialog = false;
    },

    copyContextKey(item) {
      navigator.clipboard.writeText(item?.keySyntax || item.raw.keySyntax);
    }
  }
};
</script>

<style scoped>
#context--data-table :deep(.v-data-table-header__content) {
  font-weight: bold;
}
</style>
